import React, { useState } from 'react';
import {
  Card,
  Input,
  Button,
  Typography,
  Space,
  Tabs,
  List,
  Avatar,
  Tag,
  Empty,
  Spin,
  Select,
  DatePicker,
  Row,
  Col,
  Divider,
} from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  ClearOutlined,
  ProductOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  FileTextOutlined,
  TeamOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import moment from 'moment';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const Search = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [filters, setFilters] = useState({
    category: '',
    status: '',
    dateRange: null,
  });

  const [searchResults, setSearchResults] = useState({
    products: [
      {
        id: 1,
        type: 'product',
        title: 'Wireless Headphones',
        description: 'High-quality wireless headphones with noise cancellation',
        sku: 'WH-001',
        price: 99.99,
        stock: 45,
        category: 'Electronics',
      },
      {
        id: 2,
        type: 'product',
        title: 'Bluetooth Speaker',
        description: 'Portable Bluetooth speaker with excellent sound quality',
        sku: 'BS-002',
        price: 79.99,
        stock: 8,
        category: 'Electronics',
      },
    ],
    customers: [
      {
        id: 1,
        type: 'customer',
        title: 'John Doe',
        description: 'john.doe@email.com',
        phone: '+1 (555) 123-4567',
        totalOrders: 5,
        totalSpent: 1250.00,
      },
      {
        id: 2,
        type: 'customer',
        title: 'Jane Smith',
        description: 'jane.smith@company.com',
        phone: '+1 (555) 234-5678',
        totalOrders: 3,
        totalSpent: 850.00,
      },
    ],
    orders: [
      {
        id: 1,
        type: 'order',
        title: 'Order ORD-2024-001',
        description: 'Order for John Doe',
        total: 199.98,
        status: 'completed',
        date: '2024-01-15',
      },
      {
        id: 2,
        type: 'order',
        title: 'Order ORD-2024-002',
        description: 'Order for Jane Smith',
        total: 79.99,
        status: 'processing',
        date: '2024-01-16',
      },
    ],
    vendors: [
      {
        id: 1,
        type: 'vendor',
        title: 'Tech Supplies Inc',
        description: 'contact@techsupplies.com',
        category: 'Electronics',
        rating: 4.5,
        totalOrders: 15,
      },
    ],
    locations: [
      {
        id: 1,
        type: 'location',
        title: 'Main Warehouse',
        description: '123 Industrial Blvd, City, State 12345',
        code: 'WH-001',
        capacity: 10000,
        currentStock: 8500,
      },
    ],
  });

  const performSearch = async (query) => {
    if (!query.trim()) return;
    
    setLoading(true);
    try {
      // Simulate API search
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Filter results based on search query
      const filteredResults = {
        products: searchResults.products.filter(item =>
          item.title.toLowerCase().includes(query.toLowerCase()) ||
          item.description.toLowerCase().includes(query.toLowerCase()) ||
          item.sku.toLowerCase().includes(query.toLowerCase())
        ),
        customers: searchResults.customers.filter(item =>
          item.title.toLowerCase().includes(query.toLowerCase()) ||
          item.description.toLowerCase().includes(query.toLowerCase())
        ),
        orders: searchResults.orders.filter(item =>
          item.title.toLowerCase().includes(query.toLowerCase()) ||
          item.description.toLowerCase().includes(query.toLowerCase())
        ),
        vendors: searchResults.vendors.filter(item =>
          item.title.toLowerCase().includes(query.toLowerCase()) ||
          item.description.toLowerCase().includes(query.toLowerCase())
        ),
        locations: searchResults.locations.filter(item =>
          item.title.toLowerCase().includes(query.toLowerCase()) ||
          item.description.toLowerCase().includes(query.toLowerCase()) ||
          item.code.toLowerCase().includes(query.toLowerCase())
        ),
      };
      
      setSearchResults(filteredResults);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
    performSearch(value);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setFilters({
      category: '',
      status: '',
      dateRange: null,
    });
    // Reset to original results
    setSearchResults({
      products: [
        {
          id: 1,
          type: 'product',
          title: 'Wireless Headphones',
          description: 'High-quality wireless headphones with noise cancellation',
          sku: 'WH-001',
          price: 99.99,
          stock: 45,
          category: 'Electronics',
        },
        {
          id: 2,
          type: 'product',
          title: 'Bluetooth Speaker',
          description: 'Portable Bluetooth speaker with excellent sound quality',
          sku: 'BS-002',
          price: 79.99,
          stock: 8,
          category: 'Electronics',
        },
      ],
      customers: [
        {
          id: 1,
          type: 'customer',
          title: 'John Doe',
          description: 'john.doe@email.com',
          phone: '+1 (555) 123-4567',
          totalOrders: 5,
          totalSpent: 1250.00,
        },
        {
          id: 2,
          type: 'customer',
          title: 'Jane Smith',
          description: 'jane.smith@company.com',
          phone: '+1 (555) 234-5678',
          totalOrders: 3,
          totalSpent: 850.00,
        },
      ],
      orders: [
        {
          id: 1,
          type: 'order',
          title: 'Order ORD-2024-001',
          description: 'Order for John Doe',
          total: 199.98,
          status: 'completed',
          date: '2024-01-15',
        },
        {
          id: 2,
          type: 'order',
          title: 'Order ORD-2024-002',
          description: 'Order for Jane Smith',
          total: 79.99,
          status: 'processing',
          date: '2024-01-16',
        },
      ],
      vendors: [
        {
          id: 1,
          type: 'vendor',
          title: 'Tech Supplies Inc',
          description: 'contact@techsupplies.com',
          category: 'Electronics',
          rating: 4.5,
          totalOrders: 15,
        },
      ],
      locations: [
        {
          id: 1,
          type: 'location',
          title: 'Main Warehouse',
          description: '123 Industrial Blvd, City, State 12345',
          code: 'WH-001',
          capacity: 10000,
          currentStock: 8500,
        },
      ],
    });
  };

  const getIcon = (type) => {
    switch (type) {
      case 'product':
        return <ProductOutlined />;
      case 'customer':
        return <UserOutlined />;
      case 'order':
        return <ShoppingCartOutlined />;
      case 'vendor':
        return <TeamOutlined />;
      case 'location':
        return <ShopOutlined />;
      default:
        return <FileTextOutlined />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'processing':
        return 'blue';
      case 'pending':
        return 'orange';
      case 'cancelled':
        return 'red';
      default:
        return 'default';
    }
  };

  const getAllResults = () => {
    return [
      ...searchResults.products,
      ...searchResults.customers,
      ...searchResults.orders,
      ...searchResults.vendors,
      ...searchResults.locations,
    ];
  };

  const getTabResults = (tab) => {
    if (tab === 'all') return getAllResults();
    return searchResults[tab] || [];
  };

  const tabItems = [
    {
      key: 'all',
      label: `All (${getAllResults().length})`,
    },
    {
      key: 'products',
      label: `Products (${searchResults.products.length})`,
    },
    {
      key: 'customers',
      label: `Customers (${searchResults.customers.length})`,
    },
    {
      key: 'orders',
      label: `Orders (${searchResults.orders.length})`,
    },
    {
      key: 'vendors',
      label: `Vendors (${searchResults.vendors.length})`,
    },
    {
      key: 'locations',
      label: `Locations (${searchResults.locations.length})`,
    },
  ];

  const renderListItem = (item) => {
    const actions = [];
    
    if (item.type === 'product') {
      actions.push(
        <Text key="price">${item.price}</Text>,
        <Text key="stock">Stock: {item.stock}</Text>
      );
    } else if (item.type === 'customer') {
      actions.push(
        <Text key="orders">{item.totalOrders} orders</Text>,
        <Text key="spent">${item.totalSpent}</Text>
      );
    } else if (item.type === 'order') {
      actions.push(
        <Text key="total">${item.total}</Text>,
        <Tag key="status" color={getStatusColor(item.status)}>
          {item.status}
        </Tag>
      );
    } else if (item.type === 'vendor') {
      actions.push(
        <Text key="rating">★ {item.rating}</Text>,
        <Text key="orders">{item.totalOrders} orders</Text>
      );
    } else if (item.type === 'location') {
      actions.push(
        <Text key="code">{item.code}</Text>,
        <Text key="utilization">
          {Math.round((item.currentStock / item.capacity) * 100)}% full
        </Text>
      );
    }

    return (
      <List.Item actions={actions}>
        <List.Item.Meta
          avatar={<Avatar icon={getIcon(item.type)} />}
          title={
            <div className="flex items-center space-x-2">
              <Text strong>{item.title}</Text>
              <Tag color="blue">{item.type}</Tag>
            </div>
          }
          description={
            <div>
              <Text>{item.description}</Text>
              {item.sku && (
                <>
                  <br />
                  <Text type="secondary">SKU: {item.sku}</Text>
                </>
              )}
              {item.phone && (
                <>
                  <br />
                  <Text type="secondary">Phone: {item.phone}</Text>
                </>
              )}
              {item.date && (
                <>
                  <br />
                  <Text type="secondary">Date: {moment(item.date).format('MMM D, YYYY')}</Text>
                </>
              )}
              {item.category && (
                <>
                  <br />
                  <Tag color="orange">{item.category}</Tag>
                </>
              )}
            </div>
          }
        />
      </List.Item>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <Title level={2} className="mb-0">
          <SearchOutlined className="mr-2" />
          Search
        </Title>
        <Text type="secondary">
          Search across all your inventory data
        </Text>
      </div>

      <Card className="mb-6">
        <Space.Compact style={{ width: '100%' }} size="large">
          <Input
            placeholder="Search products, customers, orders, vendors, locations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onPressEnter={(e) => handleSearch(e.target.value)}
            prefix={<SearchOutlined />}
            size="large"
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            size="large"
            onClick={() => handleSearch(searchQuery)}
            loading={loading}
          >
            Search
          </Button>
          <Button
            icon={<ClearOutlined />}
            size="large"
            onClick={clearSearch}
          >
            Clear
          </Button>
        </Space.Compact>

        <Divider />

        <Row gutter={16}>
          <Col span={6}>
            <Select
              placeholder="Category"
              value={filters.category}
              onChange={(value) => setFilters(prev => ({ ...prev, category: value }))}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="Electronics">Electronics</Option>
              <Option value="Accessories">Accessories</Option>
              <Option value="Clothing">Clothing</Option>
            </Select>
          </Col>
          <Col span={6}>
            <Select
              placeholder="Status"
              value={filters.status}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
              <Option value="pending">Pending</Option>
              <Option value="completed">Completed</Option>
            </Select>
          </Col>
          <Col span={8}>
            <RangePicker
              value={filters.dateRange}
              onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={4}>
            <Button
              type="default"
              icon={<FilterOutlined />}
              style={{ width: '100%' }}
            >
              Apply Filters
            </Button>
          </Col>
        </Row>
      </Card>

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems.map(item => ({
            ...item,
            children: (
              <Spin spinning={loading}>
                {getTabResults(item.key).length === 0 ? (
                  <Empty
                    description={
                      searchQuery
                        ? `No results found for "${searchQuery}"`
                        : 'Enter a search query to find results'
                    }
                  />
                ) : (
                  <List
                    itemLayout="horizontal"
                    dataSource={getTabResults(item.key)}
                    renderItem={renderListItem}
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} results`,
                    }}
                  />
                )}
              </Spin>
            ),
          }))}
        />
      </Card>
    </div>
  );
};

export default Search;
